import { Injectable } from '@angular/core';
import { TExcelContext } from '@monorepo/types';
import { TMMFeatureSet, TUploadResponseDTO, UnifiedLoginService } from '@monorepo/services';
import { ModalController } from '@ionic/angular';
import { UploadSummaryModalComponent } from '../app/components/common/upload-summary-modal/upload-summary-modal.component';
import { UploadModalComponent } from '../app/components/common/upload-modal/upload-modal.component';
import {
  SingleSelectMobileComponent,
  TAccordionActionInfo,
  TBulkActionInfo,
  TOPTION_LIST_ITEM,
  TSingleSelectMobileModalDismissEmitVal,
} from '@monorepo/ui';
import { ROLE_BASED_ACTION_HELPER, TSparePartBulkActions } from './role-based-action-helper';
import { map } from 'rxjs';
import { NavigationFacade } from '../redux-store/navigation/navigation.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { MachineListFacade } from '../redux-store';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ListingHelperClass {
  constructor(
    private modalCtrl: ModalController,
    private unifiedLoginService: UnifiedLoginService,
    private navigationFacade: NavigationFacade,
    private machineListFacade: MachineListFacade,
    private translate: TranslateService,
  ) {}

  async openUploadModal(excelContext: TExcelContext) {
    const uploadModal = await this.modalCtrl.create({
      component: UploadModalComponent,
      componentProps: {
        excelContext,
      },
      cssClass: 'm-modal__upload-modal',
    });

    await uploadModal.present();
  }

  async openUploadSummaryModal(summary: TUploadResponseDTO, excelContext: TExcelContext) {
    this.modalCtrl.dismiss();
    const uploadSummaryModal = await this.modalCtrl.create({
      component: UploadSummaryModalComponent,
      componentProps: {
        summary,
        excelContext,
      },
      cssClass: 'm-modal__upload-summary-modal',
    });

    await uploadSummaryModal.present();
  }

  getMachineIssueTicketActionDisplayConf(): TAccordionActionInfo[] {
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'MARK_AS_COMPLETED',
        displayName: 'tm_container_actions.MarkCompleted',
        isHidden: !features.has('TICKET_REPAIR_COMPLETED'),
      },
      {
        actionId: 'RAISE_REQUEST',
        displayName: 'tm_container_actions.RaiseRequest',
        isHidden: !features.has('SPARE_PART_RAISE_REQUEST'),
      },
      {
        actionId: 'RE_ASSIGN_TICKET',
        displayName: 'tm_container_actions.Re-Assign',
        isHidden: !features.has('TICKET_ASSIGN_ENGINEER'),
      },
      {
        actionId: 'APPROVE_TICKET',
        displayName: 'tm_container_actions.Approve',
        isHidden: !features.has('TICKET_APPROVAL'),
      },
      {
        actionId: 'START_REPAIR',
        displayName: 'tm_container_actions.StartRepair',
        isHidden: !features.has('TICKET_REPAIR_START'),
      },
      {
        actionId: 'UPDATE_MACHINE_ISSUE',
        displayName: 'tm_container_actions.UpdateIssueDetails',
        isHidden: !features.has('TICKET_DETAILS'),
      },
      {
        actionId: 'REPLACE_MACHINE',
        displayName: 'tm_container_actions.ReplaceMachine',
        isHidden: !features.has('TICKET_MACHINE_REPLACE'),
      },
      {
        actionId: 'REISSUE_TICKET',
        displayName: 'tm_container_actions.ReIssueTicket',
        isHidden: !features.has('TICKET_APPROVAL'),
      },
      {
        actionId: 'VIEW_REQUEST_DETAILS',
        displayName: 'tm_container_actions.ViewRequestDetails',
        isHidden: !features.has('MAINTENANCE_TICKET_VIEW'),
      },
    ];
  }

  getMaintenanceTicketActionDisplayConf(): TAccordionActionInfo[] {
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'EDIT_CHECKLIST',
        displayName: 'tm_container_actions.EditChecklist',
        isHidden: !features.has('MAINTENANCE_TICKET_EDIT'),
      },
      {
        actionId: 'COMPLETE_TASK',
        displayName: 'tm_container_actions.CompleteTask',
        isHidden: !features.has('TICKET_REPAIR_COMPLETED'),
      },
      {
        actionId: 'RAISE_REQUEST',
        displayName: 'tm_container_actions.RaiseRequest',
        isHidden: !features.has('SPARE_PART_RAISE_REQUEST'),
      },
      {
        actionId: 'START_TASK',
        displayName: 'tm_container_actions.StartTask',
        isHidden: !features.has('TICKET_REPAIR_START'),
      },
      {
        actionId: 'RE_ASSIGN_TICKET',
        displayName: 'tm_container_actions.Re-AssignTask',
        isHidden: !features.has('MAINTENANCE_TICKET_ASSIGN_ENGINEER'),
      },
      {
        actionId: 'VIEW_REQUEST_DETAILS',
        displayName: 'tm_container_actions.ViewRequestDetails',
        isHidden: !features.has('MAINTENANCE_TICKET_VIEW'),
      },
    ];
  }

  roleBasedMachineListBulkActionConf(
    isDisabled: boolean,
    isSelectedFactoryDefaultFactory: boolean,
  ): TBulkActionInfo<string>[] {
    const roleBasedActionConf = ROLE_BASED_ACTION_HELPER.getRoleBasedActionConf('MACHINE_LISTING');
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'GENERATE_QR_CODE',
        displayName: 'machine_listing.GenerateQrCode',
        isDisabled,
      },
      {
        actionId: 'UPDATE_STATUS',
        displayName: 'machine_listing.UpdateStatus',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['UPDATE_STATUS']?.featureCode &&
            !features.has(roleBasedActionConf['UPDATE_STATUS']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'ASSIGN_LOCATIONS',
        displayName: 'machine_listing.AssignLocations',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['ASSIGN_LOCATIONS']?.featureCode &&
            !features.has(roleBasedActionConf['ASSIGN_LOCATIONS']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'BULK_UPDATE_PREV_SCH',
        displayName: 'machine_listing.BulkUpdatePrevSch',
        isHidden: !isSelectedFactoryDefaultFactory,
        isDisabled,
      },
      {
        actionId: 'ASSET_MOVEMENT',
        displayName: 'machine_listing.AssetMovement',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['ASSET_MOVEMENT']?.featureCode &&
            !features.has(roleBasedActionConf['ASSET_MOVEMENT']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'FLOATING_AREA',
        displayName: 'machine_listing.FloatingArea',
        isHidden: !isSelectedFactoryDefaultFactory,
        isDisabled,
      },
      {
        actionId: 'UPDATE_QR_PRINTED_STATUS',
        displayName: 'machine_listing.UpdateQRPrintedStatus',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['UPDATE_QR_PRINTED_STATUS']?.featureCode &&
            !features.has(roleBasedActionConf['UPDATE_QR_PRINTED_STATUS']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'RETURN_TO_VENDOR',
        displayName: 'machine_listing.ReturnToVendor',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['RETURN_TO_VENDOR']?.featureCode &&
            !features.has(roleBasedActionConf['RETURN_TO_VENDOR']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'DELETE_ROW',
        displayName: 'listing_common.DeleteRow',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['DELETE_ROW']?.featureCode &&
            !features.has(roleBasedActionConf['DELETE_ROW']?.featureCode)),
        isDisabled,
      },
    ];
  }

  roleBasedReturnedMachineListBulkActionConf(
    isDisabled: boolean,
    isSelectedFactoryDefaultFactory: boolean,
  ): TBulkActionInfo<string>[] {
    const roleBasedActionConf = ROLE_BASED_ACTION_HELPER.getRoleBasedActionConf('MACHINE_LISTING');
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'MOVE_BACK_TO_FACTORY',
        displayName: 'returned_machine_listing.MoveBackToFactory',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['MOVE_BACK_TO_FACTORY']?.featureCode &&
            !features.has(roleBasedActionConf['MOVE_BACK_TO_FACTORY']?.featureCode)),
        isDisabled,
      },
    ];
  }

  roleBasedLocationListingManagementBulkActionConf(
    isDisabled: boolean,
    isSelectedFactoryDefaultFactory: boolean,
  ): TBulkActionInfo<string>[] {
    const roleBasedActionConf = ROLE_BASED_ACTION_HELPER.getRoleBasedActionConf('LOCATION_LISTING');
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'ASSIGN_EMPLOYEES',
        displayName: 'location_mng.AssignEmployees',
        isHidden:
          roleBasedActionConf['ASSIGN_EMPLOYEES']?.featureCode &&
          !features.has(roleBasedActionConf['ASSIGN_EMPLOYEES']?.featureCode),
        isDisabled,
      },
      {
        actionId: 'DELETE_ROW',
        displayName: 'listing_common.DeleteRow',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['DELETE_ROW']?.featureCode &&
            !features.has(roleBasedActionConf['DELETE_ROW']?.featureCode)),
        isDisabled,
      },
      {
        actionId: 'MAP_LOCAL_NEEDLE_STORES',
        displayName: 'location_mng.MapLocalNeedleStores',
        isHidden:
          !isSelectedFactoryDefaultFactory ||
          (roleBasedActionConf['MAP_LOCAL_NEEDLE_STORES']?.featureCode &&
            !features.has(roleBasedActionConf['MAP_LOCAL_NEEDLE_STORES']?.featureCode)),
      },
    ];
  }

  sparePartListBulkActionConf(isDisabled: boolean): TBulkActionInfo<TSparePartBulkActions>[] {
    const roleBasedActionConf = ROLE_BASED_ACTION_HELPER.getRoleBasedActionConf('SPAREPART_LISTING');
    const features = new Set(this.unifiedLoginService.getFeatureSet() as TMMFeatureSet[]);

    return [
      {
        actionId: 'UPDATE_THRESHOLD',
        displayName: 'sparepart_listing.UpdateThreshold',
        isHidden:
          roleBasedActionConf['UPDATE_SPAREPART_THRESHOLD']?.featureCode &&
          !features.has(roleBasedActionConf['UPDATE_SPAREPART_THRESHOLD']?.featureCode),
        isDisabled,
      },
      {
        actionId: 'UPDATE_CRITICAL',
        displayName: 'sparepart_listing.UpdateCritical',
        isHidden:
          roleBasedActionConf['UPDATE_SPAREPART_CRITICAL']?.featureCode &&
          !features.has(roleBasedActionConf['UPDATE_SPAREPART_CRITICAL']?.featureCode),
        isDisabled,
      },
      {
        actionId: 'DELETE_ROW',
        displayName: 'sparepart_listing.Delete',
        isHidden:
          roleBasedActionConf['DELETE_SPAREPART']?.featureCode &&
          !features.has(roleBasedActionConf['DELETE_SPAREPART']?.featureCode),
        isDisabled,
      },
    ];
  }

  isSelectedFactoryDefaultFactory = toSignal(
    this.navigationFacade.getSelectedFactoryIds().pipe(
      map((selected) => {
        if (selected.length === 0) return true;

        const defaultFactoryId = this.unifiedLoginService.getUserInfo().factoryId;
        return selected.length === 1 && defaultFactoryId === selected[0];
      }),
    ),
    { initialValue: true },
  );

  async getUserSelectedMachine(factorySerialNumber: string) {
    const res = await this.machineListFacade.getMachinesBySerialNumber(factorySerialNumber);

    if (!res || res.machines.length === 0) return null;

    if (res.machines.length === 1) return res.machines[0].machineId;

    const optionList: TOPTION_LIST_ITEM[] = res.machines.map((m) => ({
      displayName: this.translate.instant('tm_common.InitialFactory', { factory: m.initialFactoryName }),
      key: String(m.machineId),
    }));
    const singleSelectModalComponent = await this.modalCtrl.create({
      component: SingleSelectMobileComponent,
      cssClass: 'm-modal-mobile__initial-machine-factory',
      componentProps: { title: 'tm_common.SelectMachine', optionList },
      backdropDismiss: true,
    });
    await singleSelectModalComponent.present();

    const dismiss = (await singleSelectModalComponent.onDidDismiss()) as TSingleSelectMobileModalDismissEmitVal;
    if (dismiss.role === 'ON_SELECTED' && dismiss.data !== undefined) return Number(dismiss.data);

    return null;
  }
}
